import { studioIdOf } from '../utils/studioOf';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ChevronLeft,
  ChevronRight,
  AccessTime,
  HourglassTop,
  NotificationsActive,
  Warning,
  Close,
  PeopleAlt,
  PersonAdd,
  ArrowUpward,
  ArrowDownward,
  DeleteOutline,
  Add,
} from '@mui/icons-material';
import apiService from '../services/apiService';

interface WaitlistUser {
  _id: string;
  name: string;
  phoneNumber: string;
  email?: string;
  image?: string;
}

interface WaitlistEntry {
  _id: string;
  position: number;
  status: 'waiting' | 'notified';
  bookingSource: string;
  addedBy?: 'member' | 'studio';
  addedByName?: string;
  joinedAt?: string;
  createdAt?: string;
  notifiedAt?: string;
  user: WaitlistUser;
}

interface SessionWithWaitlist {
  _id: string;
  /** The session's location (id or populated) — waitlist actions are sent there. */
  studio?: any;
  className: string;
  date: string;
  slots: number;
  bookedCount: number;
  availableSlots: number;
  instructor?: { name: string; image?: string };
  classType?: { name: string };
  isLive?: boolean;
  duration?: number;
  waitlistCount: number;
  waitingCount: number;
  notifiedCount: number;
  waitlist: WaitlistEntry[];
}

const APP_SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  purple_pilates: { label: 'Purple Pilates', color: '#7b1fa2' },
  mobile_app: { label: 'SweatSocial', color: '#1565c0' },
  sweatsocial: { label: 'SweatSocial', color: '#1565c0' },
  website: { label: 'Website', color: '#2e7d32' },
  admin: { label: 'Admin', color: '#e65100' },
  house_of_pilates: { label: 'House of Pilates', color: '#8d6e63' },
  poise_and_pulse: { label: 'Poise & Pulse', color: '#b9875f' },
  laya_pilates: { label: 'Laya', color: '#00897b' },
  coreline: { label: 'Coreline', color: '#37474f' },
  ember_and_ice: { label: 'Ember & Ice', color: '#d84315' },
  chakra: { label: 'Chakra', color: '#6a1b9a' },
  chakra_raipur: { label: 'Chakra Raipur', color: '#6a1b9a' },
  rebel: { label: 'Rebél', color: '#89010A' },
  partner_api: { label: 'Partner API', color: '#4e342e' },
};

function toISTDateString(date: Date): string {
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  const ist = new Date(date.getTime() + IST_OFFSET_MS);
  const y = ist.getUTCFullYear();
  const m = String(ist.getUTCMonth() + 1).padStart(2, '0');
  const d = String(ist.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDisplayDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
}

function timeAgo(isoString?: string): string {
  if (!isoString) return '';
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000 / 60);
  if (diff < 1) return 'just now';
  if (diff < 60) return `${diff}m ago`;
  const h = Math.floor(diff / 60);
  return `${h}h ${diff % 60}m ago`;
}

function isNotificationStale(entry: WaitlistEntry): boolean {
  if (entry.status !== 'notified' || !entry.notifiedAt) return false;
  const minsElapsed = (Date.now() - new Date(entry.notifiedAt).getTime()) / 1000 / 60;
  return minsElapsed > 60;
}

const SessionCard: React.FC<{ session: SessionWithWaitlist; onClick: () => void; onAdd: () => void }> = ({ session, onClick, onAdd }) => {
  const started = new Date(session.date).getTime() <= Date.now();
  const hasWaitlist = session.waitlistCount > 0;
  const fillPct = session.slots > 0 ? Math.round((session.bookedCount / session.slots) * 100) : 0;
  const hasStaleNotif = session.waitlist.some(isNotificationStale);

  return (
    <Card
      onClick={onClick}
      sx={{
        border: '1px solid',
        borderColor: hasWaitlist ? 'rgba(233,30,99,0.25)' : 'divider',
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'all 0.18s ease',
        '&:hover': {
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          borderColor: hasWaitlist ? '#e91e63' : 'primary.main',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Class name + draft badge */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.75}>
          <Typography variant="body2" fontWeight={700} sx={{ flex: 1, lineHeight: 1.3 }} noWrap>
            {session.className}
          </Typography>
          {!session.isLive && (
            <Chip label="Draft" size="small" sx={{ ml: 1, fontSize: 10, height: 18, bgcolor: '#fff3e0', color: '#e65100', flexShrink: 0 }} />
          )}
          {!started && (
            <Tooltip title="Add a member to this waitlist">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onAdd(); }}
                sx={{ ml: 0.5, mt: -0.5, mr: -0.5, color: '#e91e63' }}
                aria-label="Add member to waitlist"
              >
                <Add fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Time + duration + instructor */}
        <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
          <AccessTime sx={{ fontSize: 12, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary" noWrap>
            {formatTime(session.date)}{session.duration ? ` · ${session.duration}m` : ''}
          </Typography>
        </Box>

        {/* Fill bar */}
        <Box mb={1}>
          <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'grey.100', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${Math.min(fillPct, 100)}%`, bgcolor: fillPct >= 100 ? '#e91e63' : 'primary.main', borderRadius: 2, transition: 'width 0.3s' }} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            {session.bookedCount}/{session.slots} booked
          </Typography>
        </Box>

        {/* Waitlist badges */}
        <Box display="flex" gap={0.5} flexWrap="wrap">
          {session.waitingCount > 0 && (
            <Chip
              icon={<HourglassTop sx={{ fontSize: '11px !important' }} />}
              label={`${session.waitingCount} waiting`}
              size="small"
              sx={{ fontSize: 10, height: 20, bgcolor: '#fff8e1', color: '#f57f17', fontWeight: 600 }}
            />
          )}
          {session.notifiedCount > 0 && (
            <Chip
              icon={<NotificationsActive sx={{ fontSize: '11px !important' }} />}
              label={`${session.notifiedCount} notified`}
              size="small"
              sx={{ fontSize: 10, height: 20, bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
            />
          )}
          {hasStaleNotif && (
            <Tooltip title="Has notified entry >1h old">
              <Warning sx={{ fontSize: 16, color: '#f57f17', alignSelf: 'center' }} />
            </Tooltip>
          )}
          {!hasWaitlist && (
            <Chip label="No waitlist" size="small" sx={{ fontSize: 10, height: 20, bgcolor: '#f5f5f5', color: '#9e9e9e' }} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

interface WarningItem { code: string; message: string }
interface AddCheck {
  canAdd: boolean;
  blockers: WarningItem[];
  warnings: WarningItem[];
  nextPosition: number;
}
interface MemberOption { _id: string; name?: string; email?: string; phoneNumber?: string }

// apiService's interceptor flattens every failure to { message, status, data }
// (data = the server's body) — there is no `e.response` on what callers see.
const errorBody = (e: any) => e?.data ?? e?.response?.data;
const errorStatus = (e: any) => e?.status ?? e?.response?.status;
const errorMessage = (e: any, fallback: string) => {
  const m = errorBody(e)?.message ?? e?.message;
  return Array.isArray(m) ? m.join(' ') : (m || fallback);
};

const WarningList: React.FC<{ items: WarningItem[]; severity: 'error' | 'warning' }> = ({ items, severity }) =>
  items.length ? (
    <Alert severity={severity} sx={{ mt: 1.5, '& ul': { m: 0, pl: 2 } }}>
      <ul>{items.map(i => <li key={i.code}><Typography variant="body2">{i.message}</Typography></li>)}</ul>
    </Alert>
  ) : null;

const WaitlistDialog: React.FC<{
  session: SessionWithWaitlist | null;
  startInAddMode: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}> = ({ session, startInAddMode, onClose, onChanged }) => {
  const [order, setOrder] = useState<WaitlistEntry[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Add-member panel
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<MemberOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [member, setMember] = useState<MemberOption | null>(null);
  const [check, setCheck] = useState<AddCheck | null>(null);
  const [checking, setChecking] = useState(false);

  // Confirmations
  const [bookConfirm, setBookConfirm] = useState<{ entry: WaitlistEntry; warnings: WarningItem[] } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<WaitlistEntry | null>(null);
  const [removeReason, setRemoveReason] = useState('');

  const sessionId = session?._id;
  useEffect(() => {
    setAdding(startInAddMode);
    setError(null);
    setNotice(null);
    setMember(null);
    setCheck(null);
    setQuery('');
    setDirty(false);
  }, [sessionId, startInAddMode]);

  // Keep the local order in step with the server unless staff are mid-edit.
  useEffect(() => {
    if (session && !dirty) setOrder(session.waitlist);
  }, [session, dirty]);

  // Member search (debounced)
  useEffect(() => {
    if (!adding || query.trim().length < 2) { setOptions([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res: any = await apiService.searchUsers(query.trim());
        setOptions(res?.data || []);
      } catch { setOptions([]); } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query, adding]);

  // As soon as a member is picked, ask the server what adding them would mean.
  useEffect(() => {
    if (!member || !sessionId) { setCheck(null); return; }
    let live = true;
    setChecking(true);
    apiService.checkWaitlistAdd(sessionId, member._id, studioIdOf(session?.studio))
      .then((r: any) => { if (live) setCheck(r); })
      .catch((e: any) => { if (live) setError(errorMessage(e, 'Could not check this member.')); })
      .finally(() => { if (live) setChecking(false); });
    return () => { live = false; };
  }, [member, sessionId, session?.studio]);

  if (!session) return null;
  const started = new Date(session.date).getTime() <= Date.now();
  const hasFreeSeat = session.availableSlots > 0;

  const run = async (fn: () => Promise<any>, done: string) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      await fn();
      setNotice(done);
      await onChanged();
      return true;
    } catch (e: any) {
      setError(errorMessage(e, 'Something went wrong.'));
      return false;
    } finally { setBusy(false); }
  };

  const handleAdd = async (confirm = check ? check.warnings.length > 0 : false) => {
    if (!member || !check) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const res: any = await apiService.addToWaitlist(session._id, member._id, confirm, studioIdOf(session.studio));
      setNotice(`${member.name || 'Member'} added at position #${res?.position ?? check.nextPosition}.`);
      setMember(null); setCheck(null); setQuery(''); setDirty(false);
      await onChanged();
    } catch (e: any) {
      const data = errorBody(e);
      // The window moved between the check and the click: show the new
      // warnings and let staff confirm, instead of a dead-end error.
      if (errorStatus(e) === 409 && data?.warnings) setCheck({ ...check, warnings: data.warnings });
      else setError(errorMessage(e, 'Could not add this member.'));
    } finally { setBusy(false); }
  };

  const move = (index: number, delta: number) => {
    const j = index + delta;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[index], next[j]] = [next[j], next[index]];
    setOrder(next);
    setDirty(true);
  };

  const saveOrder = async () => {
    const ok = await run(() => apiService.reorderWaitlist(session._id, order.map(e => e._id), studioIdOf(session.studio)), 'Queue order saved.');
    setDirty(false);
    // On failure (usually "the waitlist changed meanwhile") show the fresh list.
    if (!ok) await onChanged();
  };

  const bookNow = async (entry: WaitlistEntry, confirm = false) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      await apiService.bookFromWaitlist(entry._id, confirm, studioIdOf(session.studio));
      setBookConfirm(null);
      setNotice(`${entry.user?.name || 'Member'} is booked into this class.`);
      await onChanged();
    } catch (e: any) {
      const data = errorBody(e);
      if (errorStatus(e) === 409 && data?.warnings) setBookConfirm({ entry, warnings: data.warnings });
      else { setBookConfirm(null); setError(errorMessage(e, 'Could not book this member.')); }
    } finally { setBusy(false); }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    const target = removeTarget;
    const ok = await run(
      () => apiService.removeFromWaitlist(target._id, removeReason.trim() || undefined, studioIdOf(session.studio)),
      `${target.user?.name || 'Member'} removed from the waitlist.`,
    );
    if (ok) { setRemoveTarget(null); setRemoveReason(''); setDirty(false); }
  };

  return (
    <>
      <Dialog open={!!session} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, gap: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>{session.className}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTime(session.date)}{session.duration ? ` · ${session.duration} min` : ''}
              {session.instructor ? ` · ${session.instructor.name}` : ''}
              {` · ${session.bookedCount}/${session.slots} booked`}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {!started && !adding && (
              <Tooltip title={dirty ? 'Save or undo the new order first' : ''}><span>
                <Button size="small" variant="contained" startIcon={<PersonAdd />} disabled={dirty} onClick={() => setAdding(true)}>
                  Add member
                </Button>
              </span></Tooltip>
            )}
            <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, maxHeight: '70vh', overflowY: 'auto' }}>
          {(error || notice) && (
            <Box px={2} pt={1.5}>
              {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
              {notice && !error && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
            </Box>
          )}

          {adding && !started && (
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" fontWeight={700}>Add a member to this waitlist</Typography>
                <Button size="small" onClick={() => { setAdding(false); setMember(null); setCheck(null); setQuery(''); }}>Cancel</Button>
              </Box>
              <Autocomplete
                size="small"
                options={options}
                loading={searching}
                value={member}
                filterOptions={x => x}
                getOptionLabel={(o) => [o.name, o.phoneNumber].filter(Boolean).join(' · ')}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                onInputChange={(_, v, reason) => { if (reason === 'input') setQuery(v); }}
                onChange={(_, v) => { setMember(v); setError(null); }}
                noOptionsText={query.trim().length < 2 ? 'Type at least 2 letters' : 'No members found'}
                renderOption={(props, o) => (
                  <li {...props} key={o._id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{o.name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{[o.phoneNumber, o.email].filter(Boolean).join(' · ')}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} placeholder="Search member by name, phone or email" autoFocus />}
              />
              {checking && <Box display="flex" alignItems="center" gap={1} mt={1.5}><CircularProgress size={16} /><Typography variant="caption">Checking…</Typography></Box>}
              {check && !checking && (
                <>
                  <WarningList items={check.blockers} severity="error" />
                  <WarningList items={check.warnings} severity="warning" />
                  {check.canAdd && (
                    <Box display="flex" alignItems="center" justifyContent="space-between" mt={1.5} gap={1} flexWrap="wrap">
                      <Typography variant="body2" color="text.secondary">
                        Will be added at position <b>#{check.nextPosition}</b>
                      </Typography>
                      <Button
                        variant="contained"
                        color={check.warnings.length ? 'warning' : 'primary'}
                        disabled={busy || dirty}
                        onClick={() => handleAdd(check.warnings.length > 0)}
                      >
                        {check.warnings.length ? 'Add anyway' : 'Add to waitlist'}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}

          {order.length === 0 ? (
            <Box textAlign="center" py={4}>
              <PeopleAlt sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">No members on the waitlist for this class.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ width: 40, fontWeight: 600, color: 'text.secondary', fontSize: 12 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12 }}>Member</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12 }}>Added</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.map((entry, index) => {
                  const appInfo = APP_SOURCE_LABELS[entry.bookingSource] || { label: entry.bookingSource, color: '#555' };
                  const stale = isNotificationStale(entry);
                  const joinedTime = entry.createdAt || entry.joinedAt;
                  return (
                    <TableRow key={entry._id} sx={{ bgcolor: stale ? 'rgba(245,127,23,0.08)' : 'transparent' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} color={index === 0 ? '#e91e63' : 'text.primary'}>
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar src={entry.user?.image} sx={{ width: 26, height: 26, fontSize: 11, bgcolor: '#e91e63' }}>
                            {entry.user?.name?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">{entry.user?.name || '—'}</Typography>
                            <Chip label={appInfo.label} size="small"
                              sx={{ fontSize: 10, height: 18, bgcolor: `${appInfo.color}18`, color: appInfo.color, fontWeight: 600 }} />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{entry.user?.phoneNumber || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {joinedTime ? timeAgo(joinedTime) : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entry.addedBy === 'studio' ? `by ${entry.addedByName || 'staff'}` : 'by member'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {entry.status === 'waiting'
                            ? <Chip label="Waiting" size="small" sx={{ bgcolor: '#fff8e1', color: '#f57f17', fontSize: 11, height: 20 }} />
                            : <Chip label="Notified" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: 11, height: 20 }} />}
                          {stale && (
                            <Tooltip title="Notified over an hour ago">
                              <Warning sx={{ fontSize: 15, color: '#f57f17' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Move up"><span>
                          <IconButton size="small" disabled={busy || index === 0} onClick={() => move(index, -1)}><ArrowUpward fontSize="small" /></IconButton>
                        </span></Tooltip>
                        <Tooltip title="Move down"><span>
                          <IconButton size="small" disabled={busy || index === order.length - 1} onClick={() => move(index, 1)}><ArrowDownward fontSize="small" /></IconButton>
                        </span></Tooltip>
                        <Tooltip title={started ? 'Class has started' : !hasFreeSeat ? 'Class is full — free a seat first' : dirty ? 'Save the order first' : 'Book this member into the class now'}><span>
                          <Button size="small" sx={{ ml: 0.5 }} disabled={busy || started || !hasFreeSeat || dirty} onClick={() => bookNow(entry)}>Book now</Button>
                        </span></Tooltip>
                        <Tooltip title={dirty ? 'Save the order first' : 'Remove from waitlist'}><span>
                          <IconButton size="small" color="error" disabled={busy || dirty} onClick={() => { setRemoveTarget(entry); setRemoveReason(''); }}><DeleteOutline fontSize="small" /></IconButton>
                        </span></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {session.waitlistCount} on the waitlist · {session.waitingCount} waiting · {session.notifiedCount} notified
          </Typography>
          {dirty && (
            <>
              <Button size="small" disabled={busy} onClick={() => { setOrder(session.waitlist); setDirty(false); }}>Undo</Button>
              <Button size="small" variant="contained" disabled={busy} onClick={saveOrder}>Save order</Button>
            </>
          )}
          <Button size="small" onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Book-now confirmation (outside the automatic window) */}
      <Dialog open={!!bookConfirm} onClose={() => setBookConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Book {bookConfirm?.entry.user?.name || 'this member'} anyway?</DialogTitle>
        <DialogContent>
          <WarningList items={bookConfirm?.warnings || []} severity="warning" />
          <Typography variant="body2" mt={1.5}>One class will be taken from their pack and they will be told they are booked.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={busy} onClick={() => bookConfirm && bookNow(bookConfirm.entry, true)}>Book anyway</Button>
        </DialogActions>
      </Dialog>

      {/* Remove confirmation */}
      <Dialog open={!!removeTarget} onClose={() => setRemoveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove {removeTarget?.user?.name || 'this member'} from the waitlist?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={1.5}>Everyone below them moves up one place. The member is notified.</Typography>
          <TextField fullWidth size="small" label="Reason (optional, saved in the audit log)" value={removeReason} onChange={e => setRemoveReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={busy} onClick={confirmRemove}>Remove</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const SessionWaitlistPage: React.FC = () => {
  const [dateStr, setDateStr] = useState<string>(toISTDateString(new Date()));
  const [data, setData] = useState<{ sessions: SessionWithWaitlist[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addMode, setAddMode] = useState(false);

  const load = useCallback(async (d: string, quiet = false) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const res = await apiService.getSessionWaitlists(d);
      setData(res);
    } catch (e: any) {
      setError(errorMessage(e, 'Failed to load waitlist data'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { load(dateStr); }, [dateStr, load]);

  const shiftDate = (delta: number) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const next = new Date(y, m - 1, d + delta);
    setDateStr(toISTDateString(next));
  };

  const sessions = data?.sessions || [];
  const selectedSession = sessions.find(s => s._id === selectedId) || null;
  const openSession = (id: string, add: boolean) => { setAddMode(add); setSelectedId(id); };
  const visibleSessions = showAll ? sessions : sessions.filter(s => s.waitlistCount > 0);

  const totalWaiting = sessions.reduce((a, s) => a + s.waitingCount, 0);
  const totalNotified = sessions.reduce((a, s) => a + s.notifiedCount, 0);
  const sessionsWithWaitlist = sessions.filter(s => s.waitlistCount > 0).length;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>Session Waitlist</Typography>

      {/* Date Navigator */}
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <Box sx={{ display: 'inline-flex', alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <IconButton onClick={() => shiftDate(-1)} size="small" sx={{ borderRadius: 0, px: 1.5 }}>
            <ChevronLeft />
          </IconButton>
          <Box
            component="input"
            type="date"
            value={dateStr}
            onChange={(e: any) => setDateStr(e.target.value)}
            style={{ border: 'none', outline: 'none', fontSize: 14, fontWeight: 600, padding: '7px 12px', backgroundColor: 'transparent', cursor: 'pointer' }}
          />
          <IconButton onClick={() => shiftDate(1)} size="small" sx={{ borderRadius: 0, px: 1.5 }}>
            <ChevronRight />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">{formatDisplayDate(dateStr)}</Typography>
      </Box>

      {/* Summary chips */}
      {!loading && data && (
        <Box display="flex" gap={1.5} mb={3} flexWrap="wrap">
          {[
            { label: 'Sessions', value: sessions.length, color: '#1565c0' },
            { label: 'With Waitlist', value: sessionsWithWaitlist, color: '#e91e63' },
            { label: 'Waiting', value: totalWaiting, color: '#f57f17' },
            { label: 'Notified', value: totalNotified, color: '#2e7d32' },
          ].map(c => (
            <Box key={c.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, border: '1px solid', borderColor: `${c.color}30`, borderRadius: 2, bgcolor: `${c.color}08` }}>
              <Typography variant="subtitle2" fontWeight={800} color={c.color}>{c.value}</Typography>
              <Typography variant="caption" color="text.secondary">{c.label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Toggle */}
      {!loading && sessions.length > 0 && (
        <Box mb={2}>
          <FormControlLabel
            control={<Switch checked={showAll} onChange={e => setShowAll(e.target.checked)} size="small" />}
            label={<Typography variant="body2">Show all sessions ({sessions.length} total)</Typography>}
          />
        </Box>
      )}

      {/* Grid */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : sessions.length === 0 ? (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No sessions scheduled for this date.</Typography>
        </Box>
      ) : visibleSessions.length === 0 ? (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No waitlisted users for any session today.</Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Toggle "Show all sessions" to see all {sessions.length} sessions — use the + on a class to add a member to its waitlist.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {visibleSessions.map(session => (
            <Grid key={session._id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <SessionCard
                session={session}
                onClick={() => openSession(session._id, false)}
                onAdd={() => openSession(session._id, true)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Waitlist detail modal */}
      <WaitlistDialog
        session={selectedSession}
        startInAddMode={addMode}
        onClose={() => setSelectedId(null)}
        onChanged={() => load(dateStr, true)}
      />
    </Box>
  );
};

export default SessionWaitlistPage;
